import { useRef, useState } from "react";
import { cn } from "@repo/ui/lib/utils";
import {
  Configure,
  Index,
  SearchBox,
  useAutocomplete,
} from "react-instantsearch";
import { useTranslations } from "next-intl";
import Icon from "@repo/ui/components/ui/icon/icon";
import {
  Command,
  CommandItem,
  CommandList,
} from "@repo/ui/components/ui/command";

const SUGGESTIONS_INDEX = "products_query_suggestions";
const SUGGESTIONS_INDEX_ID = "suggestions";

type SuggestionHit = {
  objectID: string;
  query: string;
};

function SuggestionsList({
  hidden,
  onSelect,
}: {
  hidden: boolean;
  onSelect?: (query: string) => void;
}) {
  const { indices, currentRefinement, refine } = useAutocomplete();

  const suggestions = indices.find((i) => i.indexId === SUGGESTIONS_INDEX_ID);
  const hits: SuggestionHit[] =
    currentRefinement.length >= 2 && suggestions
      ? (suggestions.hits as unknown as SuggestionHit[])
      : [];

  if (hidden || hits.length === 0) return null;

  return (
    <CommandList
      className={cn(
        "w-full absolute left-0 right-0 top-full z-20 mt-4",
        "rounded-[30px] border p-4 max-md:p-2 text-primary-black bg-primary-white dark:bg-primary-white drop-shadow-product-image",
        "hidden group-focus-within:block",
        "max-h-none overflow-visible",
      )}
    >
      {hits.map((hit) => (
        <CommandItem
          key={hit.objectID}
          value={hit.objectID}
          onSelect={() => {
            refine(hit.query);
            onSelect?.(hit.query);
          }}
          className={cn(
            "w-full text-left text-base flex items-center gap-3 px-4 py-3 max-md:py-2 rounded-md transition-colors cursor-pointer",
            "data-[selected=true]:bg-secondary-gray2 dark:data-[selected=true]:bg-secondary-gray2 dark:data-[selected=true]:text-primary-black",
            "hover:bg-secondary-gray2 dark:hover:bg-secondary-gray2",
            "border-b last:border-b-0",
          )}
        >
          <Icon
            icon="Search"
            className="h-[16px] w-[16px] stroke-secondary-black/50 shrink-0"
          />
          <div className="flex-1">{hit.query}</div>
          <Icon
            icon="ArrowSquare"
            className="rotate-90 h-[26px] w-[26px] fill-secondary-black/50 stroke-secondary-black/50 shrink-0"
          />
        </CommandItem>
      ))}
    </CommandList>
  );
}

export default function AlgoliaSearchBox({
  onSearch,
  onSuggestionSelect,
  autofocus = false,
}: {
  onSearch?: (e: React.FormEvent<HTMLFormElement>) => void;
  onSuggestionSelect?: (query: string) => void;
  autofocus?: boolean;
}) {
  const t = useTranslations("Search");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userNavigatedRef = useRef(false);
  const commandRef = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState(false);

  const handleQueryHook = (query: string, search: (q: string) => void) => {
    userNavigatedRef.current = false;
    setDismissed(false);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const value = query.trim();
      if (value.length >= 2) {
        search(value);
      }
    }, 250);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      userNavigatedRef.current = true;
    } else if (e.key === "Enter" && !userNavigatedRef.current) {
      e.stopPropagation();
    }
  };

  return (
    <Command
      ref={commandRef}
      shouldFilter={false}
      className={cn(
        "relative w-full group",
        "h-auto rounded-none bg-transparent dark:bg-transparent overflow-visible",
        "text-inherit dark:text-inherit",
      )}
    >
      <SearchBox
        autoFocus={autofocus}
        placeholder={t("what_searching")}
        onSubmit={onSearch}
        inputProps={{ name: "search", onKeyDown: handleKeyDown }}
        queryHook={handleQueryHook}
        submitIconComponent={({ classNames }) => (
          <Icon
            className={cn(
              "absolute left-0 top-0 cursor-pointer stroke-primary-magenta fill-primary-magenta flex items-center px-[30px] max-md:px-6 h-full *:h-[24px] *:w-[24px] *:max-md:h-[16px] *:max-md:w-[16px]",
              classNames.submitIcon,
            )}
            icon="Search"
          />
        )}
        resetIconComponent={() => <></>}
        classNames={{
          root: "w-full mt-5 text-left",
          form: "relative flex flex-row",
          input: cn(
            "w-full flex h-[70px] px-3 rounded-lg bg-secondary-gray2 dark:bg-primary-white text-primary-black py-2 text-xl ring-offset-background",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed",
            "disabled:opacity-50",
            "rounded-[100px] px-10 max-md:h-[60px] bg-secondary-gray2 dark:bg-primary-white text-primary-black max-md:text-base",
            "pl-[84px] max-md:pl-[64px]",
          ),
        }}
      />
      <Index indexName={SUGGESTIONS_INDEX} indexId={SUGGESTIONS_INDEX_ID}>
        <Configure hitsPerPage={10} />
      </Index>
      <SuggestionsList
        hidden={dismissed}
        onSelect={(query) => {
          setDismissed(true);
          commandRef.current
            ?.querySelector<HTMLInputElement>("input[type='search']")
            ?.blur();
          onSuggestionSelect?.(query);
        }}
      />
    </Command>
  );
}
